import { asyncAction } from "../common"
import { limsRequest } from "./limsRequest"

// Per-sample sample-prep worklists (F9, SPEC-041 / PRD-008; BE companion SPEC-024; read-only
// instrument + consumables: SPEC-042 / BE SPEC-026). The analyst's EXECUTED sample-prep checklist
// for ONE sample of ONE analysis run, retrievable per (sampleId, analysisId). Instantiated
// (fetch-or-create) from the EFFECTIVE worklist template for the analysis definition; the server
// auto-carries the effective forskrift version + the analysis's instrument + consumables (all
// READ-ONLY — they belong to the analysis, not picked here); the analyst checks off the steps +
// adds notes and SIGNS it (capability `analyst`, NO four-eyes — the analyst signs their own prep).
//
// A signed worklist is a PRECONDITION (not an entry gate): a result cannot be sent on from the
// technical reviewer (to supervisor OR QA) and the sample is not QA-complete until the worklist is
// signed — but ONLY for analyses whose definition has an effective worklist template (the gate is
// enforced server-side in results.py / release.py, mirrored fail-fast in the FE via
// selectIsWorklistSigned + selectHasUnsignedRequiredWorklist).
//
// Persisted via /lims/sample-worklists. GET ?sampleId=&analysisId= is fetch-or-create and returns
// { item } (the equipment is derived server-side from the analysis). PATCH saves progress (no
// password) or analyst-signs (state:"signed" + password). With showLims off this slice is inert.

// SPEC-024 — the per-sample worklist state machine (mirrors the backend SAMPLE_WORKLIST_STATES).
export const SAMPLE_WORKLIST_STATES = { DRAFT: "draft", SIGNED: "signed" }

const initialState = { items: [], isLoading: false }

// Stable map key for one (sampleId, analysisId) pair — the worklist is keyed per sample-analysis.
export const worklistKey = (sampleId, analysisId) => `${sampleId || ""}|${analysisId || ""}`

function upsert(items, item) {
  if (!item) return items
  const exists = items.some((x) => x.id === item.id)
  return exists ? items.map((x) => (x.id === item.id ? item : x)) : [...items, item]
}

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "limsSampleWorklists/fetch/BEGIN":
      return { ...state, isLoading: true }
    case "limsSampleWorklists/fetch":
      return { ...state, items: action.res || [], isLoading: false }
    // fetchOrCreate / save / sign return the { item } envelope; we upsert the record. The instrument
    // + consumables are derived/frozen server-side from the analysis, so there are no asset hints.
    case "limsSampleWorklists/upsert": {
      const env = action.res || {}
      return {
        ...state,
        items: upsert(state.items, env.item),
      }
    }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

// ---- Thunks ----------------------------------------------------------------------------------

// Fetch the WHOLE team's worklists once (so the completeness selectors + the per-sample link state
// can read them without a round-trip per sample). Optional — fetchOrCreate is the primary path.
export const fetch = asyncAction("limsSampleWorklists/fetch", (_dispatch) => {
  return limsRequest("GET", "/lims/sample-worklists/all").then((res) => res.items)
})

// Fetch-or-create the worklist for one (sampleId, analysisId): the server returns the existing row
// or creates one auto-filled with the GMP context (effective template version, forskrift version,
// consumable selection, planned instrument). Returns the full envelope. `analysisDefinitionId` is an
// optional hint used only when no QC result exists yet for the pair.
export const fetchOrCreate = asyncAction(
  "limsSampleWorklists/upsert",
  (_dispatch, { sampleId, analysisId, analysisDefinitionId } = {}) => {
    const params = new URLSearchParams({ sampleId, analysisId })
    if (analysisDefinitionId) params.set("analysisDefinitionId", analysisDefinitionId)
    return limsRequest("GET", `/lims/sample-worklists?${params.toString()}`)
  }
)

// Save draft progress: the completed steps. No e-signature (the worklist is still a draft). The
// instrument + consumables are derived server-side from the analysis, not sent from here. Returns
// the { item } envelope.
export const save = asyncAction("limsSampleWorklists/upsert", (_dispatch, id, patch) => {
  return limsRequest("PATCH", `/lims/sample-worklists/${id}`, { patch })
})

// Analyst-sign the worklist (draft → signed). The signer's password + reason ride at the request-body
// TOP LEVEL; the server enforces the "analyst" capability (NO four-eyes — the analyst signs their own
// prep), re-freezes the GMP snapshots (forskrift + the analysis's instrument + consumables), writes
// the immutable signature + audit. `patch` carries the final completedSteps so the signature attests
// to the state being signed. Returns the { item } envelope.
export const sign = asyncAction("limsSampleWorklists/upsert", (_dispatch, id, signature, patch = {}) => {
  return limsRequest("PATCH", `/lims/sample-worklists/${id}`, {
    patch: { ...patch, state: SAMPLE_WORKLIST_STATES.SIGNED },
    password: (signature && signature.password) || undefined,
    reason: (signature && signature.reason) || undefined,
  })
})

// ---- Selectors -------------------------------------------------------------------------------

export const selectAllWorklists = (state) => state.limsSampleWorklists.items

// The worklist record for one (sampleId, analysisId) pair, or undefined. Retrievable — the worklist
// can always be found again on the exact sample-analysis (7a).
export const selectForSampleAnalysis = (state, sampleId, analysisId) =>
  state.limsSampleWorklists.items.find((w) => w.sampleId === sampleId && w.analysisId === analysisId)

// Whether the (sampleId, analysisId) worklist is SIGNED — the boolean the F3 send gate + the F6
// completeness check read (mirrors the server-authoritative gate). A missing row counts as unsigned.
export const selectIsWorklistSigned = (state, sampleId, analysisId) => {
  const w = selectForSampleAnalysis(state, sampleId, analysisId)
  return !!(w && w.state === SAMPLE_WORKLIST_STATES.SIGNED)
}
